import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, MicOff, PhoneOff, Volume2 } from "lucide-react";
import { API_URL } from "@/utils/apiUrl";

const COPY = {
  tr: {
    start: "Nexus ile canlı konuş",
    connecting: "Nexus bağlanıyor…",
    live: "Nexus dinliyor — doğal şekilde konuşun",
    stop: "Konuşmayı bitir",
    muted: "Mikrofon kapalı",
    unsupported: "Bu tarayıcı canlı sesli görüşmeyi desteklemiyor.",
    denied: "Mikrofon izni verilmedi.",
    failed: "Nexus canlı ses bağlantısı kurulamadı.",
  },
  en: {
    start: "Talk to Nexus live",
    connecting: "Connecting to Nexus…",
    live: "Nexus is listening — speak naturally",
    stop: "End conversation",
    muted: "Microphone muted",
    unsupported: "This browser does not support live voice conversations.",
    denied: "Microphone permission was not granted.",
    failed: "Nexus could not start the live voice connection.",
  },
};

export default function NexusRealtimeVoice({ language = "en", disabled = false, onDesignRequest, onNavigate, onInspectPage }) {
  const [status, setStatus] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState("");
  const [caption, setCaption] = useState("");
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const dataChannelRef = useRef(null);
  const handledToolCallsRef = useRef(new Set());
  const toolQueueRef = useRef(Promise.resolve());
  const onDesignRequestRef = useRef(onDesignRequest);
  const onNavigateRef = useRef(onNavigate);
  const onInspectPageRef = useRef(onInspectPage);
  const strings = COPY[language] || COPY.en;

  useEffect(() => {
    onDesignRequestRef.current = onDesignRequest;
  }, [onDesignRequest]);

  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);

  useEffect(() => {
    onInspectPageRef.current = onInspectPage;
  }, [onInspectPage]);

  const stop = () => {
    dataChannelRef.current?.close();
    peerRef.current?.close();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (audioRef.current) audioRef.current.srcObject = null;
    dataChannelRef.current = null;
    peerRef.current = null;
    streamRef.current = null;
    setMuted(false);
    setCaption("");
    setStatus("idle");
  };

  // The cleanup must run exactly once when this voice surface unmounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stop, []);

  const start = async () => {
    if (disabled || status !== "idle") return;
    if (!window.RTCPeerConnection || !navigator.mediaDevices?.getUserMedia) {
      setError(strings.unsupported);
      return;
    }
    setError("");
    setCaption("");
    setStatus("connecting");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const peer = new RTCPeerConnection();
      const audio = new Audio();
      audio.autoplay = true;
      peer.ontrack = (event) => {
        audio.srcObject = event.streams[0];
        audio.play().catch(() => {});
      };
      stream.getTracks().forEach((track) => peer.addTrack(track, stream));

      const events = peer.createDataChannel("oai-events");
      peerRef.current = peer;
      streamRef.current = stream;
      audioRef.current = audio;
      dataChannelRef.current = events;
      events.onopen = () => setStatus("live");
      events.onmessage = async (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === "input_audio_buffer.speech_started") setCaption("…");
          if (payload.type === "conversation.item.input_audio_transcription.completed") {
            setCaption(payload.transcript || "");
          }
          if (payload.type === "response.output_audio_transcript.delta") {
            setCaption((old) => `${old}${payload.delta || ""}`);
          }
          const functionCall = payload.type === "response.function_call_arguments.done"
            ? payload
            : (payload.type === "response.output_item.done" ? payload.item : null);
          if (["start_design_generation", "improve_current_design", "navigate_platform", "inspect_current_page"].includes(functionCall?.name)) {
            const callId = functionCall.call_id || functionCall.id;
            if (callId && handledToolCallsRef.current.has(callId)) return;
            if (callId) handledToolCallsRef.current.add(callId);
            const args = JSON.parse(functionCall.arguments || "{}");
            const isNavigation = functionCall.name === "navigate_platform";
            const isInspection = functionCall.name === "inspect_current_page";
            const isRevision = functionCall.name === "improve_current_design";
            const prompt = String(args.prompt || "").trim();
            const destination = String(args.destination || "").trim();
            let toolResult = {};
            if (isInspection) {
              if (typeof onInspectPageRef.current !== "function") throw new Error(strings.failed);
              setCaption(language === "tr" ? "Sayfadaki içerik inceleniyor…" : "Reading the visible page…");
              toolQueueRef.current = toolQueueRef.current.catch(() => undefined).then(async () => {
                toolResult = await onInspectPageRef.current();
              });
            } else if (isNavigation) {
              if (!destination || typeof onNavigateRef.current !== "function") throw new Error(strings.failed);
              setCaption(language === "tr" ? "İlgili bölüm açılıyor…" : "Opening that section…");
              toolQueueRef.current = toolQueueRef.current.catch(() => undefined).then(() => onNavigateRef.current(destination));
            } else {
              if (!prompt || typeof onDesignRequestRef.current !== "function") throw new Error(strings.failed);
              setCaption(language === "tr"
                ? (isRevision ? "Tasarım iyileştiriliyor…" : "Tasarım başlatılıyor…")
                : (isRevision ? "Improving the design…" : "Starting the design…"));
              toolQueueRef.current = toolQueueRef.current.catch(() => undefined).then(() => onDesignRequestRef.current(prompt, {
                mode: isRevision ? "improve" : "create",
                studioMode: args.project_mode === "pro"
                  ? "pro"
                  : (args.project_mode === "standard" ? "standard" : undefined),
              }));
            }
            await toolQueueRef.current;
            setCaption(language === "tr"
              ? (isInspection ? "Sayfa incelendi." : (isNavigation ? "Bölüm açıldı." : "Hazır — yeni bir iyileştirme söyleyebilirsiniz."))
              : (isInspection ? "Page inspected." : (isNavigation ? "Section opened." : "Ready — you can request another improvement.")));
            if (callId && events.readyState === "open") {
              events.send(JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: callId,
                  output: JSON.stringify(isInspection ? {
                    status: "completed",
                    ...toolResult,
                  } : {
                    status: "completed",
                    action: isNavigation ? "navigate" : (isRevision ? "improve" : "create"),
                    destination: isNavigation ? destination : undefined,
                    project_mode: isNavigation ? undefined : (args.project_mode || "standard"),
                  }),
                },
              }));
              events.send(JSON.stringify({
                type: "response.create",
                response: {
                  instructions: (
                    "Reply in the same language as the user's most recent spoken request. " +
                    "If the user spoke Turkish, reply only in Turkish and remain in Turkish " +
                    "unless the user explicitly asks to change languages. Briefly confirm " +
                    "that the requested operation completed. If page content was inspected, answer the user's " +
                    "question from that tool output without inventing missing facts. For design operations, ask " +
                    "for the next improvement."
                  ),
                },
              }));
            }
          }
          if (payload.type === "error") setError(payload.error?.message || strings.failed);
        } catch {
          // Ignore unknown realtime events; the audio session remains active.
        }
      };
      events.onerror = () => setError(strings.failed);
      peer.onconnectionstatechange = () => {
        if (["failed", "disconnected", "closed"].includes(peer.connectionState)) stop();
      };

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      const token = localStorage.getItem("session_token") || sessionStorage.getItem("session_token");
      const tokenResponse = await fetch(
        `${API_URL}/api/voice/realtime/token?language=${encodeURIComponent(language)}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!tokenResponse.ok) {
        const detail = await tokenResponse.json().catch(() => ({}));
        throw new Error(detail?.detail || strings.failed);
      }
      const ephemeral = await tokenResponse.json();
      if (!ephemeral?.value) throw new Error(strings.failed);

      // OpenAI's documented browser/mobile flow sends the original offer SDP
      // directly with a short-lived client secret. The permanent project key
      // remains on the backend and never reaches the browser.
      const response = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeral.value}`,
          "Content-Type": "application/sdp",
        },
      });
      if (!response.ok) throw new Error(strings.failed);
      await peer.setRemoteDescription({ type: "answer", sdp: await response.text() });

    } catch (cause) {
      stop();
      setError(cause?.name === "NotAllowedError" ? strings.denied : (cause?.message || strings.failed));
      setStatus("error");
    }
  };

  const toggleMute = () => {
    const next = !muted;
    streamRef.current?.getAudioTracks().forEach((track) => { track.enabled = !next; });
    setMuted(next);
  };

  if (status === "idle" || status === "error") {
    return (
      <div className="mb-6">
        <button
          type="button"
          onClick={start}
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded-2xl bg-cyan-300 px-5 py-3 font-medium text-[#061016] disabled:opacity-40"
        >
          <Mic size={19} /> {strings.start}
        </button>
        {error && <p className="mt-2 text-sm text-red-200" role="alert">{error}</p>}
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-3xl border border-cyan-300/20 bg-cyan-300/[0.06] p-4" aria-live="polite">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-cyan-100">
          {status === "connecting" ? <Loader2 className="animate-spin" size={18} /> : <Volume2 size={18} />}
          {status === "connecting" ? strings.connecting : (muted ? strings.muted : strings.live)}
        </div>
        {status === "live" && (
          <>
            <button type="button" onClick={toggleMute} aria-label={muted ? strings.live : strings.muted} className="ml-auto rounded-xl border border-white/10 p-2 hover:bg-white/10">
              {muted ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            <button type="button" onClick={stop} className="inline-flex items-center gap-2 rounded-xl bg-red-400/15 px-3 py-2 text-sm text-red-100 hover:bg-red-400/25">
              <PhoneOff size={17} /> {strings.stop}
            </button>
          </>
        )}
      </div>
      {caption && <p className="mt-3 text-sm leading-6 text-white/65">{caption}</p>}
    </div>
  );
}
